import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  //CircularProgress,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  //Download as DownloadIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { EstimationRecord } from './utils/persistentDB';
import { formatCostaRicanCurrency } from './utils/currencyFormatter';
import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';

interface EstimationHistoryGridProps {
  onLoadEstimation: (estimation: EstimationRecord) => void;
  onRefresh?: () => void;
  loadedEstimations: EstimationRecord[];
  claimUId: string;
}

const EstimationHistoryGrid: React.FC<EstimationHistoryGridProps> = ({
  onLoadEstimation,
  onRefresh,
  loadedEstimations,
  claimUId,
}) => {
  const [estimations, setEstimations] = useState<EstimationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    estimation: EstimationRecord | null;
  }>({ open: false, estimation: null });
  //const [downloading, setDownloading] = useState<string | null>(null);

  const loadEstimations = useCallback(() => {
    setLoading(true);
    try {
      // Eliminar duplicados basándose en el _id y filtrar solo las no eliminadas
      const uniqueEstimations = loadedEstimations
        .filter(
          (estimation, index, self) =>
            index === self.findIndex((e) => e._id === estimation._id),
        )
        .filter((estimation) => !estimation.isDeleted);

      setEstimations(uniqueEstimations);

      // Debug: Mostrar valores de las primeras estimaciones
      if (uniqueEstimations.length > 0) {
        console.log('🔍 DEBUG - Valores de estimaciones cargadas:');
        uniqueEstimations.slice(0, 2).forEach((est, index) => {
          console.log(`Estimación ${index + 1}:`, {
            _id: est._id,
            totalLossAmount: est.calculations?.totalLossAmount,
            finalIndemnityAmount: est.calculations?.finalIndemnityAmount,
            summary: est.summary,
            hasCalculations: !!est.calculations,
            hasSummary: !!est.summary,
          });
        });
      }
    } catch (error) {
      console.error('Error al cargar estimaciones:', error);
      // Fallback: usar solo las estimaciones del padre
      setEstimations(loadedEstimations);
    } finally {
      setLoading(false);
    }
  }, [loadedEstimations]);

  // Cargar estimaciones al montar el componente
  useEffect(() => {
    loadEstimations();
  }, [loadEstimations]);

  // Efecto adicional para forzar actualización del DataGrid cuando cambien las estimaciones
  useEffect(() => {
    console.log(
      '📊 DataGrid actualizado con',
      estimations.length,
      'estimaciones',
    );
  }, [estimations]);

  // Preparar datos para el DataGrid
  const gridData = estimations.map((estimation) => ({
    id: estimation._id,
    _id: estimation._id,
    totalLossAmount: estimation.calculations.totalLossAmount,
    initialIndemnityAmount: estimation.calculations.initialIndemnityAmount,
    finalIndemnityAmount: estimation.calculations.finalIndemnityAmount,
    // Incluir toda la estimación para las acciones
    ...estimation,
  }));

  const handleViewEstimation = (estimation: EstimationRecord) => {
    console.log('👁️ Cargando estimación:', estimation._id);
    onLoadEstimation(estimation);
  };

  const handleDeleteEstimation = (estimation: EstimationRecord) => {
    console.log(
      '🔴 Eliminando estimación:',
      estimation._id,
      ' | claimUId:',
      claimUId,
    );
    setDeleteDialog({ open: true, estimation });
  };

  const confirmDelete = async () => {
    if (deleteDialog.estimation) {
      try {
        console.log(
          '🗑️ Iniciando eliminación híbrida de estimación:',
          deleteDialog.estimation._id,
        );

        // Verificar si es la estimación activa
        /*const activeId = PersistentDB.getActiveEstimationId();
        if (activeId === deleteDialog.estimation._id) {
          PersistentDB.clearActiveEstimation();
          console.log(
            '🧹 Estimación activa eliminada, limpiando registro activo',
          );
        }*/
        try {
          // Para estimaciones de la API, necesitamos el ID numérico
          // Asumimos que está en algún campo o podemos extraerlo del _id
          if (deleteDialog?.estimation?._id) {
            await new ClaimsTrackerClient().deleteCustomEstimatedAmount(
              deleteDialog.estimation._id,
            );
            //apiDeleteSuccess = true;
            console.log('✅ Estimación eliminada de la API externa');
            // Refresh the estimations
            if (onRefresh) {
              onRefresh();
            }
          } else {
            console.log(
              'ℹ️ No se pudo extraer ID numérico para eliminación de API',
            );
          }
        } catch (apiError) {
          console.warn('⚠️ Error al eliminar de la API externa:', apiError);
          // Continuar con eliminación local aunque falle la API
        }
      } catch (error) {
        console.error('❌ Error general en eliminación:', error);
      }
    }
    setDeleteDialog({ open: false, estimation: null });
  };

  const formatDate = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('es-CR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error: any) {
      console.error('Error al formatear fecha:', error);
      return timestamp;
    }
  };

  // Usar la función de formateo de moneda costarricense centralizada
  const formatCurrency = (amount: number): string => {
    return formatCostaRicanCurrency(amount);
  };

  // Definir columnas del DataGrid
  const columns: GridColDef[] = [
    {
      field: 'timestamp',
      headerName: 'Fecha',
      minWidth: 180,
      maxWidth: 200,
      flex: 1,
      valueFormatter: (params) => formatDate(params.value),
      sortable: true,
    },
    {
      field: 'totalLossAmount',
      headerName: 'Monto Total Pérdida',
      minWidth: 150,
      maxWidth: 180,
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Chip
          label={formatCurrency(params.value)}
          color="primary"
          variant="outlined"
          size="small"
        />
      ),
      sortable: true,
    },
    {
      field: 'initialIndemnityAmount',
      headerName: 'Monto a Indemnizar Inicial',
      minWidth: 180,
      maxWidth: 220,
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Chip
          label={formatCurrency(params.value)}
          color="primary"
          variant="outlined"
          size="small"
        />
      ),
      sortable: true,
    },
    {
      field: 'finalIndemnityAmount',
      headerName: 'Total a Indemnizar',
      minWidth: 150,
      maxWidth: 180,
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Chip
          label={formatCurrency(params.value)}
          color={params.value > 0 ? 'success' : 'default'}
          variant="outlined"
          size="small"
        />
      ),
      sortable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      minWidth: 150,
      maxWidth: 200,
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      getActions: (params) => [
        /*<GridActionsCellItem
          icon={
            downloading === params.row._id ? (
              <CircularProgress size={16} color="secondary" />
            ) : (
              <DownloadIcon />
            )
          }
          label={
            downloading === params.row._id
              ? 'Descargando...'
              : 'Descargar estimación'
          }
          onClick={() => handleDownloadEstimation(params.row)}
          disabled={downloading === params.row._id}
          color="secondary"
        />,*/
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="Ver y cargar estimación"
          onClick={() => handleViewEstimation(params.row)}
          color="primary"
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Eliminar estimación"
          onClick={() => handleDeleteEstimation(params.row)}
          color="error"
        />,
      ],
    },
  ];

  // @ts-expect-error - Función mantenida para uso futuro
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleDownloadEstimation = async (estimation: EstimationRecord) => {
    //setDownloading(estimation._id || '');

    try {
      // Simular proceso de descarga
      console.log('📥 Iniciando descarga de estimación:', estimation._id);

      // Crear datos para descarga
      const downloadData = {
        id: estimation._id,
        timestamp: estimation.timestamp,
        configValues: estimation.configValues,
        inputValues: estimation.inputValues,
        conditionalVariables: estimation.conditionalVariables,
        calculations: estimation.calculations,
        summary: estimation.summary,
        metadata: {
          downloadedAt: new Date().toISOString(),
          version: '1.0.0',
          format: 'JSON',
        },
      };

      // Simular tiempo de procesamiento
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Crear y descargar archivo JSON
      const jsonString = JSON.stringify(downloadData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `estimacion_${estimation._id}_${
        new Date().toISOString().split('T')[0]
      }.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('✅ Descarga completada:', estimation._id);
    } catch (error) {
      console.error('❌ Error en descarga:', error);
    }
    /*finally {
      setDownloading(null);
    }*/
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Historial de Estimaciones
          </Typography>
          <Typography>Cargando...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" gutterBottom>
              Historial de Estimaciones
            </Typography>
            <Box>
              <Tooltip title="Actualizar">
                <IconButton onClick={onRefresh} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {estimations.length === 0 ? (
            <Alert severity="info">
              No hay estimaciones guardadas. Las estimaciones se guardarán
              automáticamente cuando completes el formulario.
            </Alert>
          ) : (
            <Box sx={{ height: 300, width: '100%' }}>
              <DataGrid
                rows={gridData}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 5 },
                  },
                }}
                pageSizeOptions={[5, 10, 20]}
                disableRowSelectionOnClick
                disableColumnMenu={false}
                disableColumnFilter={false}
                disableColumnSelector={false}
                loading={loading}
                sx={{
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, estimation: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar esta estimación?
          </Typography>
          {deleteDialog.estimation && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                <strong>Fecha:</strong>{' '}
                {formatDate(deleteDialog.estimation.timestamp)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Total a Indemnizar:</strong>{' '}
                {formatCurrency(
                  deleteDialog.estimation.summary.finalIndemnityAmount,
                )}
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta acción marcará la estimación como eliminada. Solo podrás
            guardar una nueva estimación después de eliminar la actual.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, estimation: null })}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EstimationHistoryGrid;
